import { useId, useState, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';

const STORAGE_PREFIX = 'dale-nav-fold:';

function readOpen(foldId: string): boolean {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + foldId) !== 'closed';
  } catch {
    // Private windows can refuse storage; the fold then just opens every time.
    return true;
  }
}

function writeOpen(foldId: string, open: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + foldId, open ? 'open' : 'closed');
  } catch {
    // Same as above: forgetting the choice is harmless.
  }
}

type NavFoldProps = {
  /** Remembers open or closed per viewer across visits. */
  foldId: string;
  label: string;
  /** The places inside, so a closed fold still says "you are in here". */
  paths: readonly string[];
  children: ReactNode;
};

/** A sidebar group that opens in place: the label toggles, its places sit indented under it. */
export function NavFold({ foldId, label, paths, children }: NavFoldProps) {
  const listId = useId();
  const [open, setOpen] = useState(() => readOpen(foldId));
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const holdsActive = paths.some((path) => pathname === path || pathname.startsWith(`${path}/`));

  const toggle = () => {
    const next = !open;
    setOpen(next);
    writeOpen(foldId, next);
  };

  return (
    <div className="nav-fold">
      <button
        type="button"
        className="nav-item nav-fold-toggle"
        aria-expanded={open}
        aria-controls={listId}
        data-status={!open && holdsActive ? 'active' : undefined}
        onClick={toggle}
      >
        <span className="nav-fold-label">{label}</span>
        <svg className="nav-fold-chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div id={listId} className="nav-fold-items" hidden={!open}>
        {children}
      </div>
    </div>
  );
}
